import axios from "axios";
import { NGROK } from "../../../../APIs";
import type { CloudType, Tenant, AwsAccount, AzureSubscription } from "../types/cloud";
import type {
  TotalNhisResponse,
  ActiveSecretsResponse,
  CriticalRisksResponse,
  OverallRiskScoreResponse,
  NhiLifecycleRegistryResponse,
  RotationResponse,
  StaticCredentialsResponse,
  SecretsPostureResponse,
  NhiReviewResponse,
  NhiCorrelationsResponse,
  CloudSaasCorrelation,
} from "../types/dashboard";

export const fetchTenants = async (): Promise<Tenant[]> => {
  const response = await axios.get(`${NGROK}/api/get-all-domains`, {

  });
  return response.data || [];
};

// ============= Cloud Account APIs =============

export const fetchAwsAccounts = async (tenantName: string): Promise<AwsAccount[]> => {
  const response = await axios.get(`${NGROK}/api/aws/getAllAccounts`, {

    params: { tenantName },
  });
  return response.data || [];
};

export const fetchGcpProjectIds = async (tenantName: string): Promise<string[]> => {
  const response = await axios.get(`${NGROK}/api/gcp/auth/fetchAllProjectIds`, {

    params: { tenantName },
  });
  return response.data || [];
};

export const fetchAzureSubscriptions = async (tenantName: string): Promise<AzureSubscription[]> => {
  const response = await axios.get(`${NGROK}/api/azureResources/v1/all`, {

    params: { tenantName, type: "SUBSCRIPTION" },
  });
  return response.data || [];
};

// ============= Dashboard Card APIs =============

const dashboardUrl = (cloudType: CloudType, endpoint: string) =>
  `${NGROK}/api/cloud/dashboard/${cloudType}/${endpoint}`;

const commonParams = (tenantName: string, cloudAccountId: string) => ({
  tenantName,
  cloudAccountId,
});

export const fetchTotalNhis = async (
  tenantName: string,
  cloudAccountId: string,
  cloudType: CloudType,
): Promise<TotalNhisResponse> => {
  const response = await axios.get(dashboardUrl(cloudType, "total-nhis"), {

    params: commonParams(tenantName, cloudAccountId),
  });
  return response.data;
};

export const fetchActiveSecrets = async (
  tenantName: string,
  cloudAccountId: string,
  cloudType: CloudType,
): Promise<ActiveSecretsResponse> => {
  const response = await axios.get(dashboardUrl(cloudType, "active-secrets"), {

    params: commonParams(tenantName, cloudAccountId),
  });
  return response.data;
};

export const fetchCriticalRisks = async (
  tenantName: string,
  cloudAccountId: string,
  cloudType: CloudType,
): Promise<CriticalRisksResponse> => {
  const response = await axios.get(dashboardUrl(cloudType, "critical-risks"), {

    params: commonParams(tenantName, cloudAccountId),
  });
  return response.data;
};

export const fetchOverallRiskScore = async (
  tenantName: string,
  cloudAccountId: string,
  cloudType: CloudType,
): Promise<OverallRiskScoreResponse> => {
  const response = await axios.get(dashboardUrl(cloudType, "overall-risk-score"), {

    params: commonParams(tenantName, cloudAccountId),
  });
  return response.data;
};

export const fetchNhiLifecycleRegistry = async (
  tenantName: string,
  cloudAccountId: string,
  cloudType: CloudType,
): Promise<NhiLifecycleRegistryResponse> => {
  const response = await axios.get(dashboardUrl(cloudType, "nhi-lifecycle-registry"), {

    params: commonParams(tenantName, cloudAccountId),
  });
  return response.data;
};

export const fetchRotation = async (
  tenantName: string,
  cloudAccountId: string,
  cloudType: CloudType,
): Promise<RotationResponse> => {
  const response = await axios.get(dashboardUrl(cloudType, "rotation"), {

    params: commonParams(tenantName, cloudAccountId),
  });
  return response.data;
};

export const fetchStaticCredentials = async (
  tenantName: string,
  cloudAccountId: string,
  cloudType: CloudType,
): Promise<StaticCredentialsResponse> => {
  const response = await axios.get(dashboardUrl(cloudType, "static-credentials"), {

    params: commonParams(tenantName, cloudAccountId),
  });
  return response.data;
};

export const fetchSecretsPosture = async (
  tenantName: string,
  cloudAccountId: string,
  cloudType: CloudType,
): Promise<SecretsPostureResponse> => {
  const response = await axios.get(dashboardUrl(cloudType, "secrets-posture"), {

    params: commonParams(tenantName, cloudAccountId),
  });
  return response.data;
};

export const fetchNhiReview = async (
  tenantName: string,
  cloudAccountId: string,
  cloudType: CloudType,
): Promise<NhiReviewResponse> => {
  const response = await axios.get(dashboardUrl(cloudType, "nhi-review"), {

    params: commonParams(tenantName, cloudAccountId),
  });
  return response.data;
};

export const fetchNhiCorrelations = async (
  tenantName: string,
  cloudAccountId: string,
  cloudType: CloudType,
): Promise<NhiCorrelationsResponse> => {
  const response = await axios.get(dashboardUrl(cloudType, "nhi-correlations"), {

    params: commonParams(tenantName, cloudAccountId),
  });
  return response.data;
};

export const fetchCloudSaasCorrelations = async (
  tenantName: string,
  cloudId: string,
): Promise<CloudSaasCorrelation[]> => {
  const response = await axios.get(`${NGROK}/api/cloud/cloud-saas-correlations`, {

    params: { tenantName, cloudId },
  });
  return response.data || [];
};

// ============= SaaS API =============

export const fetchConfiguredSaasApps = async (tenantName: string): Promise<any> => {
  const response = await axios.get(`${NGROK}/api/saas/get-configured-saas-apps`, {

    params: { tenantName },
  });
  return response.data || {};
};

// ============= Manual Refresh API =============

export const refreshDashboardSnapshots = async (
  tenantName: string,
  cloudAccountId: string,
  cloudType: CloudType,
): Promise<any> => {
  const response = await axios.post(`${NGROK}/api/cloud/dashboard/${cloudType}/refresh-snapshots`, null, {
    params: { tenantName, cloudAccountId },
  });
  return response.data;
};
